import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, DollarSign, Link2, Pencil, Check, X, RotateCcw, Plus, CalendarClock, AlertTriangle, History } from "lucide-react";
import RefundDialog from "./RefundDialog";
import { usePublicPricing } from "@/lib/usePublicPricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAY_METHODS = ["Square", "Cash", "Cash App", "Zelle", "Apple Pay", "Card", "Manual"];
const CHARGE_PRESETS = ["Delivery Fee", "Airport Fee", "Fuel Charge", "Cleaning Charge", "Late Fee", "Mileage Charge", "Additional Driver Fee", "Young Driver Fee", "Damage Charge", "Manual Charge"];

export default function FinancialsEditor({ booking, token, onAuthError, onSaved }) {
  const [bd, setBd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    total_price: booking.total_price || 0,
    discount: booking.discount || 0,
    amount_paid: booking.amount_paid || 0,
  });
  const [saving, setSaving] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [addingCharge, setAddingCharge] = useState(false);
  const [charge, setCharge] = useState({ label: "", amount: "" });
  const [chargeSaving, setChargeSaving] = useState(false);
  const [dr, setDr] = useState(booking.daily_rolling || null);
  const [retDate, setRetDate] = useState(booking.return_date || "");
  const [drForm, setDrForm] = useState({ rate: booking.daily_rolling?.daily_rate || "", paid_through: (booking.daily_rolling?.paid_through || booking.return_date || "").slice(0, 10) });
  const [rollDays, setRollDays] = useState(0);
  const [rollMethod, setRollMethod] = useState("");
  const [rollCollected, setRollCollected] = useState("");
  const [rollSaving, setRollSaving] = useState(false);
  const [editingRate, setEditingRate] = useState(false);
  const [newRate, setNewRate] = useState("");
  const [payingWith, setPayingWith] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  const [payQuote, setPayQuote] = useState(null);
  const [payRequestKey, setPayRequestKey] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/charge-breakdown`, auth);
      setBd(data);
      if (data.daily_rolling) { setDr(data.daily_rolling); setDrForm((p) => ({ rate: data.daily_rolling.daily_rate || p.rate, paid_through: (data.daily_rolling.paid_through || p.paid_through || "").slice(0, 10) })); }
      if (data.return_date) setRetDate(data.return_date);
      setF({ total_price: data.total_charges, discount: data.discount, amount_paid: data.amount_paid });
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!payingWith) { setPayQuote(null); return; }
    setPayRequestKey(crypto.randomUUID());
  }, [payingWith]);

  useEffect(() => {
    const amt = Number(payAmount);
    if (payingWith !== "Square" || !amt || amt <= 0) { setPayQuote(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/payments/fee-quote`, { params: { subtotal: amt, method: "square" }, ...auth })
        .then((r) => setPayQuote(r.data)).catch(() => setPayQuote(null));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payAmount, payingWith]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v === "" ? 0 : parseFloat(v) }));
  const editBalance = Math.max(0, (f.total_price || 0) - (f.discount || 0) - (f.amount_paid || 0));
  const cls = "w-24 bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1 text-sm rounded-none";

  const save = async () => {
    let reason = "";
    const chargesChanged = Math.abs((f.total_price || 0) - (bd?.total_charges || 0)) > 0.009 || Math.abs((f.discount || 0) - (bd?.discount || 0)) > 0.009;
    if (chargesChanged) {
      reason = window.prompt("Total Charges / Discount is being changed. A reason is required (who approved it and why):") || "";
      if (!reason.trim()) { toast.error("A reason is required to change Total Charges."); return; }
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/bookings/${booking.id}/financials`, { ...f, reason }, auth);
      toast.success("Financials updated.");
      setEditing(false);
      await load();
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not save financials.");
    } finally { setSaving(false); }
  };

  const addCharge = async () => {
    if (!charge.label.trim() || !Number(charge.amount)) { toast.error("Enter a label and a non-zero amount."); return; }
    setChargeSaving(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/add-charge`,
        { label: charge.label.trim(), amount: Number(charge.amount) }, auth);
      toast.success(`Charge added: ${data.label} — ${money(data.amount)}. New total ${money(data.new_total)}.`);
      setAddingCharge(false); setCharge({ label: "", amount: "" });
      await load(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not add the charge.");
    } finally { setChargeSaving(false); }
  };

  const covRate = Number(usePublicPricing().allin_coverage_day) || 25;
  const covPreset = (days) => setCharge({
    label: `All-In Coverage ($${covRate.toFixed(2)}/day × ${days} day${days !== 1 ? "s" : ""})`,
    amount: (covRate * days).toFixed(2),
  });

  const [ledger, setLedger] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [trace, setTrace] = useState(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [correctBase, setCorrectBase] = useState("");
  const [corrReason, setCorrReason] = useState("");
  const [applyingCorr, setApplyingCorr] = useState(false);
  const [cleanupWeekly, setCleanupWeekly] = useState("");
  const [cleanupReason, setCleanupReason] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [locking, setLocking] = useState(false);
  const [rc, setRc] = useState({ as_of_date: new Date().toISOString().slice(0, 10), reason: "", asserted_current_due: "0" });
  const [rcPreview, setRcPreview] = useState(null);
  const [rcBusy, setRcBusy] = useState(false);

  const runReconcilePreview = async () => {
    setRcBusy(true);
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/reconcile-preview`,
        { params: { as_of_date: rc.as_of_date, asserted_current_due: Number(rc.asserted_current_due) || 0 }, ...auth });
      setRcPreview(data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not compute the preview.");
    } finally { setRcBusy(false); }
  };

  const applyReconcile = async () => {
    if (!rc.reason.trim()) { toast.error("A reason is required to approve this reconciliation."); return; }
    if (!window.confirm(`Settle current due to $${Number(rc.asserted_current_due || 0).toFixed(2)} as of ${rc.as_of_date} (removes $${Math.abs(rcPreview.correction_amount).toFixed(2)} false charge)? Future weeks, payments, and refunds are never touched.`)) return;
    setRcBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/reconcile-apply`,
        { as_of_date: rc.as_of_date, reason: rc.reason.trim(), asserted_current_due: Number(rc.asserted_current_due) || 0 }, auth);
      toast.success(data.applied ? `Reconciled — Total Charges ${money(data.old_total)} → ${money(data.new_total)}.` : data.message);
      setRcPreview(null);
      setRc((p) => ({ ...p, reason: "" }));
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not apply the reconciliation.");
    } finally { setRcBusy(false); }
  };
  const [wr, setWr] = useState(null);
  const [wrEditing, setWrEditing] = useState(false);
  const [wrForm, setWrForm] = useState({ base_rate: "", effective_date: new Date().toISOString().slice(0, 10), reason: "" });
  const [wrSaving, setWrSaving] = useState(false);

  const loadWeeklyRate = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/weekly-rate`, auth);
      setWr(data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  useEffect(() => { loadWeeklyRate(); }, [loadWeeklyRate]);

  const saveWeeklyRate = async () => {
    const rate = Number(wrForm.base_rate);
    if (!rate || rate <= 0) { toast.error("Enter a weekly base rate greater than $0."); return; }
    setWrSaving(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/weekly-rate`,
        { base_rate: rate, effective_date: wrForm.effective_date, reason: wrForm.reason.trim() }, auth);
      setWr(data);
      setWrEditing(false);
      toast.success(`Weekly rate set to ${money(rate)}/week, effective ${wrForm.effective_date}. Past charges unchanged.`);
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not save the weekly rate.");
    } finally { setWrSaving(false); }
  };

  const clearWeeklyRate = async () => {
    if (!window.confirm("Remove the most recent agreed weekly rate on this booking? It will revert to the next applicable rate (established/premium/company default).")) return;
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/weekly-rate/clear`, {}, auth);
      setWr(data);
      toast.success("Agreed weekly rate removed.");
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not clear the weekly rate.");
    }
  };

  const lockEstablishedRate = async () => {
    const wt = trace?.weekly_reconstruction?.weekly_charge;
    const reason = window.prompt(`Lock this renter's Established Rate at ${money(wt)}/week (grandfathered)?\n\nFuture pricing-setting changes will never re-price this booking. Enter a short reason:`);
    if (reason === null) return;
    setLocking(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/established-rate`, { weekly_total: wt, action: "lock", reason: reason || "Grandfathered at established agreed rate" }, auth);
      toast.success(`Established Rate locked at ${money(wt)}/week.`);
      await refreshTrace(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not lock the rate.");
    } finally { setLocking(false); }
  };

  const approveCleanup = async () => {
    const wt = Number(cleanupWeekly || trace?.weekly_reconstruction?.weekly_charge);
    if (!(wt > 0)) { toast.error("Enter the established weekly total."); return; }
    if (!cleanupReason.trim()) { toast.error("A reason is required to approve the cleanup."); return; }
    const w = trace.weekly_reconstruction;
    if (!window.confirm(`Approve Historical Cleanup?\n\nRebuilds this booking as ${w.n_weeks} consecutive 7-day weeks × ${money(wt)} (grandfathered/locked), removes only the erroneous charges, aligns the return date to the week boundary, and applies every real payment to its week.\n\nAll payments and audit history are preserved. Nothing is simply zeroed.`)) return;
    setCleaning(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/historical-cleanup`, { weekly_total: wt, reason: cleanupReason.trim() }, auth);
      toast.success(`Cleanup applied: ${data.n_weeks} weeks × ${money(data.weekly_total)} — Total ${money(data.old_total)} → ${money(data.new_total)} (removed ${money(data.removed)}). ${data.credit > 0 ? `Credit on file ${money(data.credit)}` : `True balance ${money(data.balance)}`} · paid through ${data.paid_through}.`);
      await refreshTrace(); if (ledgerOpen) await loadLedger(); await load(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not apply the cleanup.");
    } finally { setCleaning(false); }
  };

  const refreshTrace = async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/charge-trace`, auth);
      setTrace(data);
      if (data.weekly_reconstruction) setCleanupWeekly(String(data.weekly_reconstruction.weekly_charge));
      if (data.suggested_correction) {
        setCorrectBase(String(data.suggested_correction.correct_base));
        if (!corrReason) setCorrReason(data.suggested_correction.mode === "weekly"
          ? "Weekly timeline correction — booking was billed above the correct consecutive 7-day weekly total (see Weekly Timeline Reconstruction)."
          : "Erroneous charge removed — amount was not part of the original quoted pricing (see Charge Trace findings).");
      }
    } catch { /* handled by caller toast */ }
  };

  const applyCorrection = async () => {
    const cb = Number(correctBase);
    const removeAmt = Math.round(((trace?.suggested_correction?.current_base || 0) - cb) * 100) / 100;
    if (!(cb >= 0) || removeAmt <= 0) { toast.error("Enter a correct base charge lower than the current one."); return; }
    if (!corrReason.trim()) { toast.error("A reason is required to approve the correction."); return; }
    if (!window.confirm(`Approve this correction?\n\nCurrent base: ${money(trace.suggested_correction.current_base)}\nCorrect base: ${money(cb)}\nRemoving: ${money(removeAmt)} from Total Charges\n\nThis is written to the audit log with your name, timestamp and reason. Payments are not touched.`)) return;
    setApplyingCorr(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/apply-correction`, { remove_amount: removeAmt, reason: corrReason.trim() }, auth);
      toast.success(`Correction applied: removed ${money(data.removed)} — Total ${money(data.old_total)} → ${money(data.new_total)}. ${data.credit > 0 ? `Credit on file ${money(data.credit)}` : `Balance now ${money(data.balance)}`}${(data.periods_funded || []).length ? ` · ${data.periods_funded.join("; ")}` : ""}`);
      await refreshTrace(); if (ledgerOpen) await loadLedger(); await load(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not apply the correction.");
    } finally { setApplyingCorr(false); }
  };

  const toggleTrace = async () => {
    const next = !traceOpen;
    setTraceOpen(next);
    if (next && !trace) {
      setTraceLoading(true);
      await refreshTrace();
      setTraceLoading(false);
    }
  };

  const loadLedger = async () => {
    setLedgerLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/payment-ledger`, auth);
      setLedger(data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error("Could not load the payment ledger.");
    } finally { setLedgerLoading(false); }
  };

  const toggleLedger = async () => {
    const next = !ledgerOpen;
    setLedgerOpen(next);
    if (next && !ledger) await loadLedger();
  };

  const reconcile = async () => {
    const flagged = Math.round((ledger?.unapplied_amount || 0) * 100) / 100;
    if (!window.confirm(`Reconcile ${money(flagged)} in payments that were recorded but not applied correctly?\n\nThe money is applied to the oldest unpaid rental period first, the balance recalculates, paid-through/extension statuses update, and any false unpaid reminders stop. Nothing is zeroed, duplicated or deleted.`)) return;
    setReconciling(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/reconcile-payments`, {}, auth);
      toast.success(`Reconciled: ${money(data.attached)} attached${(data.extensions_cleared || []).length ? ` — ${data.extensions_cleared.join("; ")}` : ""}. ${data.credit > 0 ? `Credit on file ${money(data.credit)}` : `Balance now ${money(data.balance)}`}.`);
      await loadLedger(); await load(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not reconcile payments.");
    } finally { setReconciling(false); }
  };

  const reversePayment = async (p) => {
    if (!window.confirm(`Reverse this ${money(p.amount)} ${String(p.method).replace(/_/g, " ")} payment?\n\n⚠ This corrects an internal payment entry. It does NOT send money to or withdraw money from the customer.\n\nThe record stays permanently visible as REVERSED — NOT COUNTED, the customer's balance and allocations recalculate, and every revenue total updates immediately.`)) return;
    const reason = window.prompt("Reversal reason (required — written to the audit trail with your name and timestamp):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reversal reason is required."); return; }
    try {
      await axios.post(`${API}/finance/ledger/${p.id}/reverse`, { reason: reason.trim() }, auth);
      toast.success(`${money(p.amount)} payment REVERSED — NOT COUNTED. No customer refund was issued.`);
      await loadLedger(); await load(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not reverse the payment.");
    }
  };

  const undoReversal = async (p) => {
    if (!window.confirm(`Undo the reversal on this ${money(p.amount)} payment?\n\nThe SAME original payment is reactivated and its allocation restored exactly — no new transaction is created.`)) return;
    const reason = window.prompt("Reason for undoing the reversal (required):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to undo a reversal."); return; }
    try {
      await axios.post(`${API}/finance/ledger/${p.id}/undo-reverse`, { reason: reason.trim() }, auth);
      toast.success(`Reversal undone — the ${money(p.amount)} payment counts again.`);
      await loadLedger(); await load(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not undo the reversal.");
    }
  };

  const recordPayment = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter the amount collected."); return; }
    const rate = Number(dr?.rates?.day_total) || Number(dr?.daily_rate) || 0;
    if (dr?.enabled && rate > 0 && amt + 0.005 < rate) {
      if (window.confirm(`Daily Rolling Rental is ON (${money(rate)}/day) and this ${money(amt)} is less than a full day.\n\nAdvance 1 rental day anyway? ${money(rate - amt)} will stay on the balance owed.\n\nOK = Advance the day (partial payment) · Cancel = record as a plain payment`)) {
        setPaySaving(true);
        try {
          const { data } = await axios.post(`${API}/bookings/${booking.id}/daily-rolling/advance`,
            { days: 1, amount: amt, method: payingWith }, auth);
          toast.success(`${money(amt)} applied to 1 day — ${money(data.charge_total - data.paid)} still owed. Paid through ${data.paid_through}.`);
          setDr(data.daily_rolling); setRetDate(data.return_date);
          setPayingWith(""); setPayAmount("");
          await load(); onSaved?.();
        } catch (err) { toast.error(err.response?.data?.detail || "Could not apply the payment."); }
        finally { setPaySaving(false); }
        return;
      }
    }
    if (dr?.enabled && rate > 0 && amt + 0.005 >= rate) {
      const n = Math.floor(amt / rate);
      if (window.confirm(`Daily Rolling Rental is ON (${money(rate)}/day).\n\nApply this ${money(amt)} payment to ${n} rental day(s)? Paid-through and the return date advance ${n} day(s) automatically.\n\nOK = Apply to Rental Days · Cancel = record as a plain payment`)) {
        setPaySaving(true);
        try {
          const { data } = await axios.post(`${API}/bookings/${booking.id}/daily-rolling/advance`,
            { amount: amt, method: payingWith }, auth);
          toast.success(`${money(amt)} applied — ${data.days} day(s) added, paid through ${data.paid_through}. Balance ${data.balance < -0.005 ? money(-data.balance) + " credit" : money(Math.max(0, data.balance))}.`);
          setDr(data.daily_rolling); setRetDate(data.return_date);
          setPayingWith(""); setPayAmount("");
          await load(); onSaved?.();
        } catch (err) { toast.error(err.response?.data?.detail || "Could not apply the payment."); }
        finally { setPaySaving(false); }
        return;
      }
    }
    setPaySaving(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/record-payment`,
        { amount: amt, method: payingWith, request_key: payRequestKey }, auth);
      const applied = (data.applied_to || []).length ? ` Applied to ${data.applied_to.join("; ")}.` : "";
      const feeNote = data.processing_fee > 0 ? ` + ${money(data.processing_fee)} processing fee (total charged ${money(data.total_charged)}).` : "";
      const auto = data.auto_extension;
      if (auto?.extended) {
        toast.success(`${money(data.amount)} ${payingWith} payment recorded${feeNote}.${applied} Auto-extended 7 days to `
          + `${auto.new_return_date} (${money(auto.weekly_total || 0)} added, funded by this credit) — balance now ${money(data.balance)}.`,
          { duration: 12000 });
      } else {
        toast.success(`${money(data.amount)} ${payingWith} payment recorded${feeNote} — ${data.credit > 0 ? `credit on file ${money(data.credit)}` : `balance now ${money(data.balance)}`}.${applied}`);
        if (auto?.attempted) {
          toast.warning(`Weekly auto-extension not applied: ${auto.reason}`, { duration: 12000 });
        }
      }
      setPayingWith(""); setPayAmount(""); setPayQuote(null);
      await load(); if (ledgerOpen) await loadLedger(); onSaved?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not record the payment.");
    } finally { setPaySaving(false); }
  };

  const editCharge = async (it) => {
    const val = window.prompt(`New amount for "${it.label}" (current ${money(it.amount)}):`, it.amount);
    if (val === null) return;
    const reason = window.prompt("Reason for this change (required, saved to the audit trail):", "");
    if (!reason) { toast.error("A reason is required."); return; }
    try {
      await axios.post(`${API}/bookings/${booking.id}/edit-charge`,
        { index: it.custom_index, amount: Number(val), reason }, auth);
      toast.success("Charge updated — history preserved.");
      await load(); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not edit the charge."); }
  };

  const voidCharge = async (it) => {
    const reason = window.prompt(`Void "${it.label}" (${money(it.amount)})? Reason (required):`, "");
    if (!reason) return;
    try {
      await axios.post(`${API}/bookings/${booking.id}/edit-charge`,
        { index: it.custom_index, amount: null, reason }, auth);
      toast.success("Charge voided — history preserved.");
      await load(); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not void the charge."); }
  };

  const copyPay = () => {
    navigator.clipboard.writeText(`${window.location.origin}/pay?b=${booking.id}`);
    toast.success("Payment link copied — send it to the customer.");
  };

  const zeroBalance = async () => {
    if (!window.confirm(`Zero out this booking's balance of ${money(balance)}? The total is adjusted to match what was collected — payment history stays. This cannot be undone.`)) return;
    const pwd = window.prompt("Management only — enter the management password to confirm:");
    if (!pwd) return;
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/zero-balance`, { management_password: pwd }, auth);
      toast.success(data.zeroed ? `Balance of ${money(data.prev_balance)} zeroed — new total ${money(data.new_total)}.` : data.message);
      await load(); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not zero the balance."); }
  };

  const saveRolling = async (enabled) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/daily-rolling`,
        { enabled, daily_rate: Number(drForm.rate) || 0, paid_through: drForm.paid_through }, auth);
      setDr(data.daily_rolling);
      toast.success(enabled ? `Daily Rolling ON — ${money(data.daily_rolling.daily_rate)}/day, paid through ${data.daily_rolling.paid_through}.` : "Daily Rolling turned off.");
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update Daily Rolling."); }
  };

  const rollAdvance = async (days, useCredit = false, collected = null, insuranceAck = false) => {
    if (!useCredit && !rollMethod) { toast.error("Pick how they paid first."); return; }
    setRollSaving(true);
    try {
      const body = useCredit ? { days, use_credit: true } : { days, method: rollMethod };
      if (!useCredit && collected !== null) body.collected = collected;
      if (insuranceAck) body.insurance_ack = true;
      const { data } = await axios.post(`${API}/bookings/${booking.id}/daily-rolling/advance`, body, auth);
      const short = data.charge_total - data.paid;
      toast.success(`+${data.days} day(s) — ${money(data.charge_total)}${useCredit ? " paid from credit" : ` · ${money(data.paid)} recorded via ${rollMethod}`}${short > 0.005 && !useCredit ? ` · ${money(short)} still owed` : ""}. Paid through ${data.paid_through}.`);
      setDr(data.daily_rolling); setRetDate(data.return_date); setRollDays(0); setRollMethod(""); setRollCollected("");
      await load(); onSaved?.();
    } catch (err) {
      if (err?.response?.status === 423 && !insuranceAck) {
        setRollSaving(false);
        if (window.confirm(`${err.response.data?.detail}\n\nProceed anyway?`)) return rollAdvance(days, useCredit, collected, true);
        return;
      }
      toast.error(err.response?.data?.detail || "Could not advance the rental.");
    }
    finally { setRollSaving(false); }
  };

  const saveRate = async () => {
    const r = Number(newRate);
    if (!r || r <= 0) { toast.error("Enter a valid daily rate."); return; }
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/daily-rolling`,
        { enabled: true, daily_rate: r, paid_through: (dr?.paid_through || "").slice(0, 10) }, auth);
      setDr(data.daily_rolling); setEditingRate(false); setRollDays(0);
      toast.success(`Daily rate changed to ${money(r)}/day.`);
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change the rate."); }
  };

  const items = bd?.items || [];
  const total = bd?.total_charges ?? (booking.total_price || 0);
  const discount = bd?.discount ?? (booking.discount || 0);
  const paid = bd?.amount_paid ?? (booking.amount_paid || 0);
  const credit = bd?.credit ?? 0;
  const balance = credit > 0.005 ? -credit : (bd?.balance ?? Math.max(0, total - discount - paid));

  return (
    <div data-testid={`booking-financials-${booking.id}`} className="mt-3 rounded-md bg-[#0A0A0A] border border-white/10 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40"><DollarSign size={12} /> Financial Actions</div>
        {!editing && (
          <div className="flex items-center gap-3">
            <button data-testid={`fin-add-charge-${booking.id}`} onClick={() => setAddingCharge((v) => !v)}
              className="text-[11px] flex items-center gap-1 text-white/50 hover:text-[#F2B705] transition-colors">
              <Plus size={11} /> Add Charge
            </button>
            <button data-testid={`fin-edit-${booking.id}`} onClick={() => setEditing(true)}
              className="text-[11px] flex items-center gap-1 text-white/50 hover:text-[#F2B705] transition-colors">
              <Pencil size={11} /> Adjust
            </button>
          </div>
        )}
      </div>

      {loading && !bd ? (
        <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : (
        <>
          {/* Itemized breakdown */}
          <div data-testid={`fin-breakdown-${booking.id}`} className="space-y-1.5">
            {items.map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className="text-white/85">{it.label}</span>
                  {it.note && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#F2B705] border border-[#F2B705]/40 rounded px-1.5 py-0.5">{it.note}</span>}
                  {it.detail && <div className="text-white/40 text-xs">{it.detail}</div>}
                </div>
                <span className={`shrink-0 tabular-nums ${it.amount < 0 ? "text-emerald-400" : "text-white/85"}`}>
                  {it.amount < 0 ? "-" : ""}{money(Math.abs(it.amount))}
                </span>
                {it.kind === "custom" && (
                  <span className="shrink-0 flex gap-1">
                    <button data-testid={`fin-charge-edit-${booking.id}-${it.custom_index}`} onClick={() => editCharge(it)}
                      className="text-[9px] font-bold text-white/40 border border-white/15 rounded px-1.5 py-0.5 hover:text-[#F2B705] hover:border-[#F2B705]/50">Edit</button>
                    <button data-testid={`fin-charge-void-${booking.id}-${it.custom_index}`} onClick={() => voidCharge(it)}
                      className="text-[9px] font-bold text-white/40 border border-white/15 rounded px-1.5 py-0.5 hover:text-red-300 hover:border-red-400/50">Void</button>
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-white/10 mt-3 pt-2 space-y-1.5">
            <Row label="Total charges" value={money(total)} />
            {discount > 0 && <Row label="Discount" value={`-${money(discount)}`} tone="emerald" />}
            <Row label="Amount paid" value={money(paid)} tone="emerald" />
            {(Number(bd?.refunded_total ?? booking.refunded_total) > 0) && <Row label="Refunded to date" value={money(bd?.refunded_total ?? booking.refunded_total)} />}
            <div className="flex items-center justify-between pt-1.5 border-t border-white/10">
              <span className="text-sm font-semibold text-white">{balance < -0.005 ? "Credit Balance" : "Balance owed"}</span>
              <span className="flex items-center gap-2">
                <span data-testid={`fin-balance-${booking.id}`} className={`font-display font-bold text-lg tabular-nums ${balance > 0.005 ? "text-red-400" : "text-green-400"}`}>{balance < -0.005 ? money(-balance) : money(Math.max(0, balance))}</span>
                {Math.abs(balance) >= 0.01 && (
                  <button data-testid={`fin-zero-balance-${booking.id}`} onClick={zeroBalance}
                    title="Management only — zero this balance"
                    className="text-[10px] font-semibold border border-red-500/40 text-red-400 px-2 py-0.5 rounded hover:bg-red-500 hover:text-white transition-colors">
                    Zero
                  </button>
                )}
              </span>
            </div>
          </div>

          {/* Record a payment — every method gets its own ledger entry */}
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1.5">Record a payment</div>
            <div className="flex flex-wrap gap-1.5">
              {PAY_METHODS.map((m) => (
                <button key={m} data-testid={`fin-pay-${m.toLowerCase().replace(" ", "-")}-${booking.id}`}
                  onClick={() => { setPayingWith(m); setPayAmount(balance > 0 ? String(balance.toFixed(2)) : ""); }}
                  className={`text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${payingWith === m ? "border-emerald-400 bg-emerald-500/15 text-emerald-300" : "border-white/15 text-white/60 hover:border-emerald-400/50 hover:text-emerald-300"}`}>
                  {m}
                </button>
              ))}
            </div>
            {payingWith && (
              <div className="flex items-end gap-2 mt-2">
                <div>
                  <label className="text-[11px] text-white/45 block">{payingWith} amount ($){payingWith === "Square" ? " — subtotal" : ""}</label>
                  <input type="number" step="0.01" data-testid={`fin-pay-amount-${booking.id}`} className={cls}
                    value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                </div>
                <button data-testid={`fin-pay-record-${booking.id}`} onClick={recordPayment} disabled={paySaving}
                  className="bg-emerald-500 text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                  {paySaving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Record {payingWith} Payment
                </button>
                <button onClick={() => { setPayingWith(""); setPayQuote(null); }} className="text-white/40 hover:text-white text-xs pb-2">Cancel</button>
              </div>
            )}
            {payQuote && (
              <div data-testid={`fin-pay-quote-${booking.id}`} className="mt-2 rounded-md border border-white/10 bg-black/30 p-2.5 text-xs space-y-1 max-w-xs">
                <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{money(payQuote.subtotal)}</span></div>
                <div className="flex justify-between text-white/60"><span>Processing Fee ({payQuote.percent}% + ${(payQuote.fixed_cents / 100).toFixed(2)})</span><span>{money(payQuote.processing_fee)}</span></div>
                <div className="flex justify-between text-white font-bold pt-1 border-t border-white/10"><span>Total Charged</span><span data-testid={`fin-pay-quote-total-${booking.id}`}>{money(payQuote.total_charged)}</span></div>
              </div>
            )}
          </div>

          {/* Payment History / Ledger */}
          <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`payment-ledger-${booking.id}`}>
            <button data-testid={`fin-ledger-toggle-${booking.id}`} onClick={toggleLedger}
              className="w-full flex items-center justify-between text-left">
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40"><History size={13} /> Payment History / Ledger</span>
              <span className="text-[11px] text-[#F2B705] font-semibold">{ledgerOpen ? "Hide" : "View"}</span>
            </button>
            {ledgerOpen && (
              <div className="mt-2">
                {ledgerLoading && <div className="flex items-center gap-2 text-white/40 text-xs py-2"><Loader2 className="animate-spin" size={13} /> Loading ledger…</div>}
                {!ledgerLoading && ledger && (
                  <>
                    {ledger.unapplied_amount > 0 && (
                      <div data-testid={`fin-ledger-unapplied-${booking.id}`} className="mb-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2.5">
                        <div className="flex items-center gap-1.5 text-amber-300 text-xs font-bold"><AlertTriangle size={13} /> UNAPPLIED PAYMENT DETECTED</div>
                        <p className="text-amber-200/80 text-[11px] mt-1">{money(ledger.unapplied_amount)} in recorded payments was never counted toward this booking's balance.</p>
                        <button data-testid={`fin-ledger-reconcile-${booking.id}`} onClick={reconcile} disabled={reconciling}
                          className="mt-1.5 bg-amber-400 text-black text-[11px] font-bold px-3 py-1.5 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                          {reconciling ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />} Reconcile Now
                        </button>
                      </div>
                    )}
                    {(ledger.payments || []).length === 0 ? (
                      <p className="text-white/35 text-[11px]">No payments recorded yet.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {ledger.payments.map((p, i) => (
                          <div key={i} data-testid={`fin-ledger-row-${booking.id}-${i}`} className={`rounded border px-2.5 py-1.5 ${p.reversed ? "border-red-500/30 bg-red-500/[0.04]" : "border-white/8 bg-white/[0.03]"}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className={`font-bold text-sm tabular-nums ${p.reversed ? "text-white/35 line-through" : p.amount < 0 ? "text-red-300" : "text-emerald-300"}`}>{p.amount < 0 ? `-${money(-p.amount)}` : money(p.amount)}</span>
                              <span className="flex items-center gap-1.5">
                                {p.reversed && <span data-testid={`fin-ledger-reversed-badge-${booking.id}-${i}`} className="text-[9px] font-bold text-red-300 border border-red-500/40 rounded px-1.5 py-0.5">REVERSED — NOT COUNTED</span>}
                                <span className="text-[10px] uppercase tracking-wide text-white/50">{String(p.method).replace(/_/g, " ")}</span>
                              </span>
                            </div>
                            <div className="text-[10px] text-white/40 mt-0.5">{p.date}</div>
                            <div className={`text-[11px] mt-0.5 ${p.reversed ? "text-white/35" : "text-white/60"}`}>{p.applied_to}</div>
                            {p.reversed && p.reversal && (
                              <div className="text-[10px] text-red-300/80 mt-0.5" data-testid={`fin-ledger-reversal-info-${booking.id}-${i}`}>
                                Reversed by {p.reversal.by} · {p.reversal.at} — {p.reversal.reason}. No customer refund issued.
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-[10px] text-white/40">{p.reversed ? "Contributes $0 to revenue and balance" : <>Running balance: {p.running_credit > 0 ? <span className="text-emerald-300">{money(p.running_credit)} credit</span> : money(p.running_balance)}</>}</span>
                              <span className="flex gap-1">
                                {p.can_reverse && (
                                  <button data-testid={`fin-reverse-payment-${booking.id}-${i}`} onClick={() => reversePayment(p)}
                                    title="Management only — bookkeeping correction, no money moves"
                                    className="text-[9px] font-bold text-white/40 border border-white/15 rounded px-1.5 py-0.5 hover:text-red-300 hover:border-red-400/50 transition-colors">
                                    Reverse Entry
                                  </button>
                                )}
                                {p.reversed && (
                                  <button data-testid={`fin-undo-reversal-${booking.id}-${i}`} onClick={() => undoReversal(p)}
                                    title="Management only — reactivates this exact payment"
                                    className="text-[9px] font-bold text-emerald-300/80 border border-emerald-500/30 rounded px-1.5 py-0.5 hover:text-emerald-200 hover:border-emerald-400/60 transition-colors">
                                    Undo Reversal
                                  </button>
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {(ledger.periods || []).length > 0 && (
                      <div className="mt-2" data-testid={`fin-ledger-periods-${booking.id}`}>
                        <div className="text-[10px] uppercase tracking-widest text-white/35 mb-1">Rental Periods / Extensions</div>
                        <div className="space-y-1">
                          {ledger.periods.map((e, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] rounded border border-white/8 bg-white/[0.02] px-2.5 py-1.5">
                              <span className="text-white/60">#{e.number} · {e.period}{e.advance_credit_applied > 0 ? <span className="text-emerald-300/80"> · {money(e.advance_credit_applied)} advance credit</span> : null}</span>
                              <span className={`font-bold ${e.payment_status === "paid" ? "text-emerald-300" : e.payment_status === "partial" ? "text-amber-300" : "text-red-300"}`}>
                                {e.payment_status === "paid" ? "PAID" : `${money(e.balance)} due`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(ledger.charges_breakdown || []).length > 1 && (
                      <div className="mt-2" data-testid={`fin-ledger-breakdown-${booking.id}`}>                        <div className="text-[10px] uppercase tracking-widest text-white/35 mb-1">Charges Breakdown</div>
                        <div className="space-y-0.5">
                          {ledger.charges_breakdown.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] px-2.5 py-0.5">
                              <span className="text-white/55">{c.label}</span>
                              <span className="text-white/75 tabular-nums">{money(c.amount)}</span>
                            </div>
                          ))}
                          <div className="flex items-center justify-between text-[11px] px-2.5 py-0.5 border-t border-white/10 pt-1">
                            <span className="text-white/70 font-semibold">Total charges</span>
                            <span className="text-white/90 font-semibold tabular-nums">{money(ledger.total_charges)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {(ledger.adjustments || []).length > 0 && (
                      <div className="mt-2" data-testid={`fin-ledger-adjustments-${booking.id}`}>
                        <div className="text-[10px] uppercase tracking-widest text-white/35 mb-1">Charge Adjustments</div>
                        <div className="space-y-0.5">
                          {ledger.adjustments.map((a, i) => (
                            <div key={i} className="text-[10px] text-white/50 px-2 py-0.5 rounded bg-white/[0.02] border border-white/8">
                              <span className="text-white/65">{a.at}</span> · {a.who} · {a.detail}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Charge Trace — read-only forensic report */}
                    <div className="mt-2">
                      <button data-testid={`fin-trace-toggle-${booking.id}`} onClick={toggleTrace}
                        className="w-full text-left rounded-md border border-sky-500/30 bg-sky-500/5 px-2.5 py-1.5 flex items-center justify-between hover:bg-sky-500/10 transition-colors">
                        <span className="text-[11px] font-semibold text-sky-300">🔎 Trace Charges — where every dollar came from & what it covered</span>
                        <span className="text-[10px] text-sky-300/70">{traceOpen ? "Hide" : "Open report"}</span>
                      </button>
                      {traceOpen && (
                        <div className="mt-2 rounded-md border border-white/10 bg-black/40 p-2.5" data-testid={`fin-trace-report-${booking.id}`}>
                          {traceLoading && <div className="flex items-center gap-2 text-white/40 text-xs py-1"><Loader2 className="animate-spin" size={12} /> Building trace…</div>}
                          {!traceLoading && trace && (
                            <>
                              <p className="text-[10px] text-white/35 italic mb-2">Read-only report — nothing on this booking has been changed.</p>
                              <div className="text-[10px] uppercase tracking-widest text-white/35 mb-1">1 · Base Charge Construction</div>
                              <div className="text-[11px] text-white/60 px-1">
                                Pickup {trace.base.pickup_date}{trace.base.pickup_time ? ` ${trace.base.pickup_time}` : ""} → original return {trace.base.original_return} · {trace.base.days} day(s) · charged <span className="text-white/90 font-semibold">{money(trace.base.charge)}</span>
                              </div>
                              {(trace.base.line_items || []).length > 0 ? (
                                <div className="mt-1 space-y-0.5">
                                  {trace.base.line_items.map((li, i) => (
                                    <div key={i} className="flex items-center justify-between text-[11px] px-2 py-0.5">
                                      <span className="text-white/55">{li.label}{li.detail ? <span className="text-white/30"> — {li.detail}</span> : null}</span>
                                      <span className="text-white/75 tabular-nums">{money(li.amount)}</span>
                                    </div>
                                  ))}
                                  {Math.abs(trace.base.unexplained) >= 0.01 && (
                                    <div className="flex items-center justify-between text-[11px] px-2 py-0.5 text-amber-300">
                                      <span>Unexplained / manual adjustment (not in original quote)</span>
                                      <span className="font-semibold tabular-nums">{money(trace.base.unexplained)}</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-[11px] text-amber-300/80 px-1 mt-1">
                                  No itemized pricing snapshot on this booking — the base amount came from a manually entered total (see edit history below).
                                  {(trace.base.daily_rate > 0 || trace.base.weekly_rate > 0) && (
                                    <span className="block text-white/50 not-italic mt-0.5">
                                      Rates stored on the booking:{trace.base.daily_rate > 0 ? ` ${money(trace.base.daily_rate)}/day` : ""}{trace.base.weekly_rate > 0 ? ` ${money(trace.base.weekly_rate)}/week` : ""} × {trace.base.days} day(s)
                                    </span>
                                  )}
                                </p>
                              )}

                              {trace.weekly_reconstruction && (
                                <>
                                  <div className="text-[10px] uppercase tracking-widest text-[#F2B705] mt-3 mb-1">Weekly Timeline Reconstruction — consecutive 7-day blocks</div>
                                  <div className="rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11px] mb-1.5" data-testid={`fin-weekly-pricing-${booking.id}`}>
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className="text-[10px] uppercase tracking-widest text-white/35">
                                        {trace.weekly_reconstruction.rate_source === "established" ? "Established Rate — Grandfathered (locked)"
                                          : trace.weekly_reconstruction.rate_source === "observed" ? "Weekly price — as actually quoted & charged"
                                          : "Correct weekly price — from this vehicle's pricing configuration"}
                                      </span>
                                      {trace.weekly_reconstruction.rate_source === "established"
                                        ? <span data-testid={`fin-grandfathered-badge-${booking.id}`} className="text-[9px] font-bold text-[#F2B705] border border-[#F2B705]/40 rounded px-1 py-px">🔒 GRANDFATHERED</span>
                                        : <button data-testid={`fin-lock-rate-${booking.id}`} onClick={lockEstablishedRate} disabled={locking}
                                            className="text-[9px] font-bold text-black bg-[#F2B705] rounded px-1.5 py-0.5 hover:bg-white transition-colors disabled:opacity-60">
                                            {locking ? "…" : "LOCK AS ESTABLISHED RATE"}
                                          </button>}
                                    </div>
                                    {(trace.weekly_reconstruction.weekly_composition || []).map((c, i) => (
                                      <div key={i} className="flex justify-between text-white/60"><span>{c.label}</span><span className="tabular-nums">{money(c.amount)}</span></div>
                                    ))}
                                    <div className="flex justify-between border-t border-white/10 pt-0.5 mt-0.5"><span className="text-white/80 font-semibold">Weekly total used</span><span className="text-white/95 font-bold tabular-nums">{money(trace.weekly_reconstruction.weekly_charge)}</span></div>
                                    {trace.weekly_reconstruction.rate_source !== "config" && Math.abs(trace.weekly_reconstruction.correct_week - trace.weekly_reconstruction.weekly_charge) > 1 && (
                                      <div className="mt-1 text-white/40 text-[10px]">Vehicle's current configured weekly: {money(trace.weekly_reconstruction.correct_week)} — informational only; this renter is not re-priced</div>
                                    )}
                                    {trace.weekly_reconstruction.rate_source === "config" && trace.weekly_reconstruction.observed_week > 0 && Math.abs(trace.weekly_reconstruction.observed_week - trace.weekly_reconstruction.correct_week) > 1 && (
                                      <div className="mt-1 text-amber-300 text-[10px]">⚠ Extensions were actually billed at {money(trace.weekly_reconstruction.observed_week)}/week — pricing-source mismatch of {money(Math.abs(trace.weekly_reconstruction.observed_week - trace.weekly_reconstruction.correct_week))}/week</div>
                                    )}
                                  </div>
                                  <div className="space-y-1" data-testid={`fin-weekly-timeline-${booking.id}`}>
                                    {trace.weekly_reconstruction.weeks.map((w, i) => (
                                      <div key={i} className="rounded border border-white/8 bg-white/[0.02] px-2 py-1.5">
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="text-white/70 font-semibold">Week {w.week} · {w.start} → {w.end}</span>
                                          <span className="flex items-center gap-2">
                                            <span className="text-white/60 tabular-nums">{money(w.applied !== undefined ? w.applied : w.paid)} of {money(w.charge)}</span>
                                            {w.status === "paid" && <span className="text-[9px] font-bold text-emerald-300 border border-emerald-500/30 rounded px-1 py-px">PAID</span>}
                                            {w.status === "short" && <span className="text-[9px] font-bold text-amber-300 border border-amber-500/40 rounded px-1 py-px">SHORT {money(-w.delta)}</span>}
                                            {w.status === "no_payment" && <span className="text-[9px] font-bold text-red-300 border border-red-500/40 rounded px-1 py-px">NO PAYMENT</span>}
                                            {w.status === "not_yet_due" && <span className="text-[9px] font-bold text-white/40 border border-white/15 rounded px-1 py-px">NOT YET DUE</span>}
                                          </span>
                                        </div>
                                        {(w.payments || []).map((wp, j) => (
                                          <div key={j} className="flex items-center justify-between text-[10px] text-white/40 px-1 mt-0.5">
                                            <span>{wp.date} · {String(wp.method).replace(/_/g, " ")}</span>
                                            <span className={`tabular-nums ${wp.amount < 0 ? "text-red-300" : ""}`}>{wp.amount < 0 ? `-${money(-wp.amount)}` : money(wp.amount)}</span>
                                          </div>
                                        ))}
                                        {w.due && w.running_position !== undefined && (
                                          <div className="flex justify-end text-[10px] px-1 mt-0.5">
                                            <span className={w.running_position < -0.005 ? "text-amber-300/80" : "text-emerald-300/80"}>
                                              running {w.running_position < -0.005 ? `shortage ${money(-w.running_position)}` : `credit ${money(w.running_position)}`}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  <div className="mt-1.5 rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11px] space-y-0.5" data-testid={`fin-weekly-summary-${booking.id}`}>
                                    <div className="flex justify-between"><span className="text-white/55">Correct paid-through date</span><span className="text-emerald-300 font-semibold">{trace.weekly_reconstruction.paid_through}{trace.weekly_reconstruction.paid_through_remainder > 0 ? ` (+${money(trace.weekly_reconstruction.paid_through_remainder)} toward next week)` : ""}</span></div>
                                    <div className="flex justify-between"><span className="text-white/55">Balance due to date</span><span className={`font-semibold ${trace.weekly_reconstruction.balance_due_today > 0.005 ? "text-amber-300" : "text-emerald-300"}`}>{trace.weekly_reconstruction.balance_due_today < -0.005 ? `${money(-trace.weekly_reconstruction.balance_due_today)} paid ahead` : money(Math.max(0, trace.weekly_reconstruction.balance_due_today))}</span></div>
                                    <div className="flex justify-between"><span className="text-white/55">Full-term balance (through {trace.weekly_reconstruction.weeks[trace.weekly_reconstruction.weeks.length - 1].end})</span><span className="text-white/85 font-semibold">{money(Math.max(0, trace.weekly_reconstruction.full_term_balance))}</span></div>
                                    <div className="flex justify-between border-t border-white/10 pt-0.5"><span className="text-white/55">Billed {money(trace.weekly_reconstruction.billed_total)} vs correct {money(trace.weekly_reconstruction.correct_total)} ({trace.weekly_reconstruction.n_weeks} × {money(trace.weekly_reconstruction.weekly_charge)})</span><span className={`font-semibold ${Math.abs(trace.weekly_reconstruction.billing_discrepancy) > 1 ? "text-red-300" : "text-emerald-300"}`}>{trace.weekly_reconstruction.billing_discrepancy > 1 ? `over-billed ${money(trace.weekly_reconstruction.billing_discrepancy)}` : Math.abs(trace.weekly_reconstruction.billing_discrepancy) <= 1 ? "matches" : `under-billed ${money(-trace.weekly_reconstruction.billing_discrepancy)}`}</span></div>
                                  </div>
                                </>
                              )}

                              <div className="text-[10px] uppercase tracking-widest text-white/35 mt-3 mb-1">2 · Payments → Period They Were Intended For</div>
                              <div className="space-y-1">
                                {(trace.periods || []).map((p, i) => (
                                  <div key={i} className="rounded border border-white/8 bg-white/[0.02] px-2 py-1.5">
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="text-white/70 font-semibold">{p.label} · {p.start} → {p.end}</span>
                                      <span className={`font-bold tabular-nums ${Math.abs(p.charge - p.intended_paid) <= 1 ? "text-emerald-300" : "text-amber-300"}`}>
                                        {money(p.intended_paid)} of {money(p.charge)}
                                      </span>
                                    </div>
                                    {(p.intended_payments || []).map((ip, j) => (
                                      <div key={j} className="flex items-center justify-between text-[10px] text-white/45 px-1 mt-0.5">
                                        <span>{ip.date} · {String(ip.method).replace(/_/g, " ")}{ip.note ? ` — ${ip.note}` : ""}</span>
                                        <span className={`tabular-nums ${ip.amount < 0 ? "text-red-300" : ""}`}>{ip.amount < 0 ? `-${money(-ip.amount)}` : money(ip.amount)}</span>
                                      </div>
                                    ))}
                                    {(p.intended_payments || []).length === 0 && <div className="text-[10px] text-red-300/70 px-1 mt-0.5">No payment on record intended for this period</div>}
                                  </div>
                                ))}
                              </div>

                              {(trace.historical_records || []).length > 0 && (
                                <>
                                  <div className="text-[10px] uppercase tracking-widest text-white/35 mt-3 mb-1">3 · Historical Payment Evidence (source records, incl. pre-ledger)</div>
                                  <div className="space-y-1" data-testid={`fin-trace-historical-${booking.id}`}>
                                    {trace.historical_records.map((h, i) => (
                                      <div key={i} className="rounded border border-white/8 bg-white/[0.02] px-2 py-1">
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="text-white/60">{h.date} · <span className="text-white/80">{h.source}</span> · {String(h.method).replace(/_/g, " ")}</span>
                                          <span className="flex items-center gap-2">
                                            <span className="text-white/80 font-semibold tabular-nums">{money(h.amount)}</span>
                                            {h.in_ledger
                                              ? <span className="text-[9px] font-bold text-emerald-300/80 border border-emerald-500/30 rounded px-1 py-px">{h.evidence_only ? "EVIDENCE" : "IN LEDGER"}</span>
                                              : <span className="text-[9px] font-bold text-red-300 border border-red-500/40 rounded px-1 py-px">NOT IN LEDGER</span>}
                                          </span>
                                        </div>
                                        {h.ref && <div className="text-[10px] text-white/35 mt-0.5 break-all">{h.ref}</div>}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}

                              {(trace.findings || []).length > 0 && (
                                <>
                                  <div className="text-[10px] uppercase tracking-widest text-white/35 mt-3 mb-1">4 · Findings</div>
                                  <ul className="space-y-1" data-testid={`fin-trace-findings-${booking.id}`}>
                                    {trace.findings.map((f, i) => (
                                      <li key={i} className={`text-[11px] px-2 py-1 rounded border ${f.includes("MATCHES") ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-200/80" : "border-amber-500/25 bg-amber-500/5 text-amber-200/85"}`}>{f}</li>
                                    ))}
                                  </ul>
                                </>
                              )}

                              {trace.weekly_reconstruction && Math.abs(trace.weekly_reconstruction.billing_discrepancy) > 1 && (
                                <div className="mt-3 rounded-md border border-[#F2B705]/40 bg-[#F2B705]/5 p-2.5" data-testid={`fin-cleanup-card-${booking.id}`}>
                                  <div className="text-[10px] uppercase tracking-widest text-[#F2B705] font-bold mb-1.5">Historical Cleanup — Requires Your Approval</div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                    <span className="text-white/55">Established weekly rate <span className="text-white/30">(editable — will be locked/grandfathered)</span></span>
                                    <span className="text-right">
                                      <input data-testid={`fin-cleanup-weekly-input-${booking.id}`} type="number" step="0.01" value={cleanupWeekly}
                                        onChange={(e) => setCleanupWeekly(e.target.value)}
                                        className="w-24 bg-transparent border-b border-[#F2B705]/50 focus:border-[#F2B705] outline-none py-0.5 text-[11px] text-right text-white font-semibold" />
                                    </span>
                                    <span className="text-white/55">Consecutive 7-day weeks (from {trace.base.pickup_date})</span>
                                    <span className="text-right text-white/90 font-semibold">{trace.weekly_reconstruction.n_weeks}</span>
                                    <span className="text-white/55">Rebuilt Total Charges</span>
                                    <span className="text-right text-white/90 font-semibold tabular-nums">{money(trace.weekly_reconstruction.n_weeks * (Number(cleanupWeekly) || 0))}</span>
                                    <span className="text-white/55">Current billed Total Charges</span>
                                    <span className="text-right text-white/70 tabular-nums line-through">{money(trace.weekly_reconstruction.billed_total)}</span>
                                    <span className="text-white/55">Erroneous charges removed</span>
                                    <span className="text-right text-red-300 font-semibold tabular-nums">-{money(Math.max(0, trace.weekly_reconstruction.billed_total - trace.weekly_reconstruction.n_weeks * (Number(cleanupWeekly) || 0)))}</span>
                                    <span className="text-white/55">Payments received (all preserved)</span>
                                    <span className="text-right text-emerald-300 font-semibold tabular-nums">{money(trace.weekly_reconstruction.total_payments)}</span>
                                    <span className="text-white/70 font-semibold">Resulting true balance</span>
                                    <span className="text-right font-bold tabular-nums" data-testid={`fin-cleanup-newbalance-${booking.id}`}>
                                      {(() => {
                                        const nb = Math.round(((trace.weekly_reconstruction.n_weeks * (Number(cleanupWeekly) || 0)) - (trace.totals?.discount || 0) - (trace.totals?.amount_paid || 0)) * 100) / 100;
                                        return nb < -0.005 ? <span className="text-emerald-300">{money(-nb)} credit</span> : <span className={nb <= 0.005 ? "text-emerald-300" : "text-amber-300"}>{money(Math.max(0, nb))}</span>;
                                      })()}
                                    </span>
                                    <span className="text-white/55">Paid through (at current weekly)</span>
                                    <span className="text-right text-emerald-300 font-semibold">{trace.weekly_reconstruction.paid_through}</span>
                                  </div>
                                  <textarea data-testid={`fin-cleanup-reason-${booking.id}`} value={cleanupReason} onChange={(e) => setCleanupReason(e.target.value)}
                                    placeholder="Reason (required — written to the audit log)"
                                    rows={2} className="mt-2 w-full bg-black/40 border border-white/15 rounded p-1.5 text-[11px] text-white/85 outline-none focus:border-[#F2B705]" />
                                  <button data-testid={`fin-cleanup-approve-${booking.id}`} onClick={approveCleanup} disabled={cleaning}
                                    className="mt-1.5 bg-[#F2B705] text-black text-[11px] font-bold px-3 py-1.5 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                                    {cleaning ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />} Approve Historical Cleanup
                                  </button>
                                  <p className="text-[10px] text-white/35 mt-1">Removes erroneous charges only. Locks the grandfathered rate, aligns the return date to the week boundary, applies every real payment to its week, stops any false reminders. Payments and audit history are fully preserved — nothing is simply zeroed.</p>
                                </div>
                              )}

                              {trace.suggested_correction && trace.suggested_correction.mode !== "weekly" && (
                                <div className="mt-3 rounded-md border border-[#F2B705]/40 bg-[#F2B705]/5 p-2.5" data-testid={`fin-correction-card-${booking.id}`}>
                                  <div className="text-[10px] uppercase tracking-widest text-[#F2B705] font-bold mb-1.5">Proposed Correction — Requires Your Approval</div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                    {trace.suggested_correction.mode === "weekly" && (trace.suggested_correction.weekly_composition || []).length > 0 && (
                                      <div className="col-span-2 text-[10px] text-white/45 mb-0.5">
                                        Weekly price used: {trace.suggested_correction.weekly_composition.map((c) => `${c.label} ${money(c.amount)}`).join(" + ")} = {money(trace.suggested_correction.weekly_charge)} (coverage assumption: {trace.suggested_correction.coverage_assumption})
                                      </div>
                                    )}
                                    <span className="text-white/55">{trace.suggested_correction.mode === "weekly" ? "Current Total Charges (billed)" : "Current base charge"}</span>
                                    <span className="text-right text-white/90 font-semibold tabular-nums">{money(trace.suggested_correction.current_base)}</span>
                                    <span className="text-white/55">{trace.suggested_correction.mode === "weekly" ? <>Correct Total Charges ({trace.suggested_correction.n_weeks} weeks × {money(trace.suggested_correction.weekly_charge)}) <span className="text-white/30">(editable)</span></> : <>Correct base charge <span className="text-white/30">(editable)</span></>}</span>
                                    <span className="text-right">
                                      <input data-testid={`fin-correction-base-input-${booking.id}`} type="number" step="0.01" value={correctBase}
                                        onChange={(e) => setCorrectBase(e.target.value)}
                                        className="w-24 bg-transparent border-b border-[#F2B705]/50 focus:border-[#F2B705] outline-none py-0.5 text-[11px] text-right text-white font-semibold" />
                                    </span>
                                    <span className="text-white/55">Difference (erroneous charge removed)</span>
                                    <span className="text-right text-red-300 font-semibold tabular-nums">-{money(Math.max(0, (trace.suggested_correction.current_base || 0) - (Number(correctBase) || 0)))}</span>
                                    <span className="text-white/55">New Total Charges</span>
                                    <span className="text-right text-white/90 font-semibold tabular-nums">{money((trace.totals?.total_charges || 0) - Math.max(0, (trace.suggested_correction.current_base || 0) - (Number(correctBase) || 0)))}</span>
                                    <span className="text-white/70 font-semibold">New true booking balance{trace.suggested_correction.mode === "weekly" ? " (full term)" : ""}</span>
                                    <span className="text-right font-bold tabular-nums" data-testid={`fin-correction-newbalance-${booking.id}`}>
                                      {(() => {
                                        const nb = Math.round((((trace.totals?.total_charges || 0) - Math.max(0, (trace.suggested_correction.current_base || 0) - (Number(correctBase) || 0))) - (trace.totals?.discount || 0) - (trace.totals?.amount_paid || 0)) * 100) / 100;
                                        return nb < -0.005 ? <span className="text-emerald-300">{money(-nb)} credit</span> : <span className={nb <= 0.005 ? "text-emerald-300" : "text-amber-300"}>{money(Math.max(0, nb))}</span>;
                                      })()}
                                    </span>
                                  </div>
                                  <textarea data-testid={`fin-correction-reason-${booking.id}`} value={corrReason} onChange={(e) => setCorrReason(e.target.value)}
                                    placeholder="Reason for this correction (required — written to the audit log)"
                                    rows={2} className="mt-2 w-full bg-black/40 border border-white/15 rounded p-1.5 text-[11px] text-white/85 outline-none focus:border-[#F2B705]" />
                                  <button data-testid={`fin-correction-approve-${booking.id}`} onClick={applyCorrection} disabled={applyingCorr}
                                    className="mt-1.5 bg-[#F2B705] text-black text-[11px] font-bold px-3 py-1.5 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                                    {applyingCorr ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />} Approve Correction
                                  </button>
                                  <p className="text-[10px] text-white/35 mt-1">Removes the erroneous charge only — payments are never touched. Logged with your name, timestamp, reason and before/after totals.</p>
                                </div>
                              )}

                              {(trace.history || []).length > 0 && (
                                <>
                                  <div className="text-[10px] uppercase tracking-widest text-white/35 mt-3 mb-1">5 · Financial Edit History</div>
                                  <div className="space-y-0.5 max-h-40 overflow-y-auto">
                                    {trace.history.map((h, i) => (
                                      <div key={i} className="text-[10px] text-white/45 px-1">
                                        <span className="text-white/60">{h.at}</span> · {h.who} · {String(h.action).replace(/_/g, " ")} — {h.detail}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-white/45 mt-2 pt-2 border-t border-white/10">
                      <span>Ledger total {money(ledger.ledger_paid)} · Counted {money(ledger.amount_paid_on_booking)}</span>
                      <span className="font-semibold text-white/70">{ledger.credit > 0 ? <span className="text-emerald-300">{money(ledger.credit)} credit on file</span> : `Balance ${money(ledger.balance)}`}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Daily Rolling Rental */}
          <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`daily-rolling-${booking.id}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40"><CalendarClock size={13} /> Daily Rolling Rental</span>
              {dr?.enabled
                ? <button data-testid={`dr-off-${booking.id}`} onClick={() => window.confirm("Turn OFF Daily Rolling for this booking?") && saveRolling(false)} className="text-[10px] font-bold border border-white/20 text-white/50 px-2 py-0.5 rounded hover:text-red-300 hover:border-red-400/50">ON — turn off</button>
                : null}
            </div>
            {!dr?.enabled ? (
              <div className="flex flex-wrap items-end gap-2 mt-2">
                <div>
                  <label className="text-[11px] text-white/45 block">Daily rate ($)</label>
                  <input type="number" step="0.01" data-testid={`dr-rate-${booking.id}`} className={cls} value={drForm.rate}
                    onChange={(e) => setDrForm((p) => ({ ...p, rate: e.target.value }))} />
                </div>
                <div>
                  <label className="text-[11px] text-white/45 block">Paid through</label>
                  <input type="date" data-testid={`dr-paidthrough-${booking.id}`} className={cls + " w-36"} value={drForm.paid_through}
                    onChange={(e) => setDrForm((p) => ({ ...p, paid_through: e.target.value }))} />
                </div>
                <button data-testid={`dr-enable-${booking.id}`} onClick={() => saveRolling(true)}
                  className="bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
                  Enable Daily Rolling
                </button>
                <p className="w-full text-white/35 text-[11px]">For approved renters paying one day at a time — every payment buys the next day, no formal extension needed.</p>
              </div>
            ) : (() => {
              const baseRate = Number(dr.daily_rate) || 0;
              const covDay = Number(dr.rates?.coverage_day) || 0;
              const waivDay = Number(dr.rates?.waiver_day) || 0;
              const rate = Number(dr.rates?.day_total) || baseRate;
              const pt = (dr.paid_through || "").slice(0, 10);
              const today = new Date().toISOString().slice(0, 10);
              const due = pt && pt <= today;
              const nextThrough = pt ? new Date(`${pt}T12:00:00`) : null;
              if (nextThrough) nextThrough.setDate(nextThrough.getDate() + 1);
              const credit = balance < -0.005 ? -balance : 0;
              return (
                <div className="mt-2 space-y-2.5">
                  {due && (
                    <div data-testid={`dr-due-flag-${booking.id}`} className="flex items-center gap-2 rounded border border-red-500/50 bg-red-500/10 px-3 py-2 text-red-300 text-xs font-black tracking-wide">
                      <AlertTriangle size={14} /> PAYMENT DUE — DAILY ROLLING RENTAL (paid through {pt})
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                    <div>
                      <span className="block text-white/40 uppercase text-[10px] tracking-wide">Daily Rate</span>
                      {editingRate ? (
                        <span className="flex items-center gap-1 mt-0.5">
                          <input type="number" step="0.01" data-testid={`dr-newrate-${booking.id}`} className="w-20 bg-black border border-white/20 rounded px-1.5 py-0.5 text-white text-xs" value={newRate} onChange={(e) => setNewRate(e.target.value)} autoFocus />
                          <button data-testid={`dr-saverate-${booking.id}`} onClick={saveRate} className="text-emerald-300 hover:text-emerald-200"><Check size={13} /></button>
                          <button onClick={() => setEditingRate(false)} className="text-white/40 hover:text-white"><X size={13} /></button>
                        </span>
                      ) : (
                        <span className="text-white font-semibold flex items-center gap-1.5">{money(rate)}
                          <button data-testid={`dr-editrate-${booking.id}`} onClick={() => { setNewRate(String(baseRate)); setEditingRate(true); }} title="Change the base daily rate" className="text-white/35 hover:text-[#F2B705]"><Pencil size={11} /></button>
                        </span>
                      )}
                      {(covDay > 0 || waivDay > 0) && (
                        <span className="block text-white/40 text-[10px] mt-0.5">{money(baseRate)} base{covDay > 0 ? ` + ${money(covDay)} All-In` : ""}{waivDay > 0 ? ` + ${money(waivDay)} waiver` : ""}</span>
                      )}
                    </div>
                    <div><span className="block text-white/40 uppercase text-[10px] tracking-wide">Paid Through</span><span data-testid={`dr-pt-${booking.id}`} className={`font-semibold ${due ? "text-red-300" : "text-[#F2B705]"}`}>{pt || "—"}</span></div>
                    <div><span className="block text-white/40 uppercase text-[10px] tracking-wide">Scheduled Return</span><span className="text-white/85 font-semibold">{(retDate || "").slice(0, 10) || "—"}</span></div>
                    <div><span className="block text-white/40 uppercase text-[10px] tracking-wide">Next Payment Due</span><span className="text-white font-semibold">{money(rate)} on {pt || "—"}</span></div>
                    <div className="col-span-2"><span className="block text-white/40 uppercase text-[10px] tracking-wide">Next payment extends through</span><span className="text-white/85 font-semibold">{nextThrough ? nextThrough.toISOString().slice(0, 10) : "—"}</span></div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[1, 2, 3, 7].map((n) => (
                      <button key={n} data-testid={`dr-plus${n}-${booking.id}`} onClick={() => setRollDays(n)}
                        className={`text-xs font-bold px-3.5 py-2 rounded-md border transition-colors ${rollDays === n ? "border-[#F2B705] bg-[#F2B705]/15 text-[#F2B705]" : "border-white/15 text-white/70 hover:border-[#F2B705]/60 hover:text-[#F2B705]"}`}>
                        + {n} Day{n !== 1 ? "s" : ""} — {money(rate * n)}
                      </button>
                    ))}
                    <button data-testid={`dr-custom-${booking.id}`} onClick={() => { const v = window.prompt("How many days?"); const n = parseInt(v); if (n >= 1) setRollDays(n); }}
                      className={`text-xs font-bold px-3.5 py-2 rounded-md border transition-colors ${rollDays > 0 && ![1, 2, 3, 7].includes(rollDays) ? "border-[#F2B705] bg-[#F2B705]/15 text-[#F2B705]" : "border-white/15 text-white/70 hover:border-[#F2B705]/60 hover:text-[#F2B705]"}`}>
                      Custom Days{rollDays > 0 && ![1, 2, 3, 7].includes(rollDays) ? ` (${rollDays})` : ""}
                    </button>
                  </div>
                  {rollDays > 0 && (
                    <div className="rounded border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-2.5">
                      <p className="text-white/80 text-xs mb-1.5">+{rollDays} day(s) = <b className="text-[#F2B705]">{money(rate * rollDays)}</b> — how did they pay?</p>
                      <div className="flex flex-wrap gap-1.5">
                        {PAY_METHODS.map((m) => (
                          <button key={m} onClick={() => setRollMethod(m)}
                            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${rollMethod === m ? "border-emerald-400 bg-emerald-500/15 text-emerald-300" : "border-white/15 text-white/60 hover:border-emerald-400/50"}`}>{m}</button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <label className="text-[11px] text-white/45">Amount collected $</label>
                        <input type="number" step="0.01" data-testid={`dr-collected-${booking.id}`} placeholder={(rate * rollDays).toFixed(2)}
                          className="w-24 bg-black border border-white/20 rounded px-2 py-1 text-white text-xs"
                          value={rollCollected} onChange={(e) => setRollCollected(e.target.value)} />
                        {rollCollected !== "" && Number(rollCollected) < rate * rollDays - 0.005 && (
                          <span className="text-orange-300 text-[11px] font-semibold">{money(rate * rollDays - Number(rollCollected))} will stay on the balance owed</span>
                        )}
                        {rollCollected !== "" && Number(rollCollected) > rate * rollDays + 0.005 && (
                          <span className="text-emerald-300 text-[11px] font-semibold">{money(Number(rollCollected) - rate * rollDays)} extra becomes credit</span>
                        )}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button data-testid={`dr-confirm-${booking.id}`} onClick={() => rollAdvance(rollDays, false, rollCollected === "" ? null : Number(rollCollected))} disabled={rollSaving || !rollMethod}
                          className="bg-emerald-500 text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
                          {rollSaving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Record {money(rollCollected === "" ? rate * rollDays : Number(rollCollected))} & Advance {rollDays} Day{rollDays !== 1 ? "s" : ""}
                        </button>
                        <button onClick={() => { setRollDays(0); setRollMethod(""); setRollCollected(""); }} className="text-white/40 hover:text-white text-xs">Cancel</button>
                      </div>
                    </div>
                  )}
                  {credit >= rate - 0.005 && rate > 0 && (
                    <button data-testid={`dr-apply-credit-${booking.id}`} onClick={() => window.confirm(`Apply ${money(rate)} of the ${money(credit)} credit to the next rental day? Paid-through moves to ${nextThrough ? nextThrough.toISOString().slice(0, 10) : ""}.`) && rollAdvance(1, true)}
                      className="w-full border border-emerald-400/50 text-emerald-300 text-xs font-bold px-4 py-2 rounded-md hover:bg-emerald-400 hover:text-black transition-colors">
                      Apply Credit to Next Rental Day — {money(rate)} of {money(credit)} credit
                    </button>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Weekly Rate — agreed weekly base rate for this customer/rental */}
          <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`weekly-rate-${booking.id}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40"><DollarSign size={13} /> Weekly Rate</span>
              <span className="text-[10px] text-white/35">Scope: this rental only — {booking.name}</span>
            </div>
            {!wr ? (
              <div className="text-white/30 text-xs mt-2">Loading…</div>
            ) : (
              <div className="mt-2 space-y-2.5">
                <div className="flex flex-wrap items-end gap-x-6 gap-y-2 text-xs">
                  <div>
                    <span className="block text-white/40 uppercase text-[10px] tracking-wide">Current Rate</span>
                    {wrEditing ? (
                      <span className="flex items-center gap-1 mt-0.5">
                        <input type="number" step="0.01" data-testid={`wr-newrate-${booking.id}`} className="w-20 bg-black border border-white/20 rounded px-1.5 py-0.5 text-white text-xs" value={wrForm.base_rate} onChange={(e) => setWrForm((p) => ({ ...p, base_rate: e.target.value }))} autoFocus />
                        <input type="date" data-testid={`wr-effdate-${booking.id}`} className="bg-black border border-white/20 rounded px-1.5 py-0.5 text-white text-xs" value={wrForm.effective_date} onChange={(e) => setWrForm((p) => ({ ...p, effective_date: e.target.value }))} />
                      </span>
                    ) : (
                      <span className="text-white font-semibold flex items-center gap-1.5">{money(wr.current.rate)}/wk
                        <button data-testid={`wr-edit-${booking.id}`} onClick={() => { setWrForm({ base_rate: String(wr.current.rate), effective_date: new Date().toISOString().slice(0, 10), reason: "" }); setWrEditing(true); }} title="Change the agreed weekly rate" className="text-white/35 hover:text-[#F2B705]"><Pencil size={11} /></button>
                      </span>
                    )}
                    <span className="block text-white/40 text-[10px] mt-0.5 capitalize">
                      {{ agreed: "Agreed rate (this booking)", established: "Established — grandfathered", premium: "Premium vehicle rate", booking_snapshot: "Original booking rate", default: "Company default" }[wr.current.source] || wr.current.source}
                      {wr.current.effective_date ? ` · since ${wr.current.effective_date}` : ""}
                    </span>
                  </div>
                  <div>
                    <span className="block text-white/40 uppercase text-[10px] tracking-wide">Company Default</span>
                    <span className="text-white/60 font-semibold">{money(wr.default_rate)}/wk</span>
                  </div>
                  {wr.upcoming && (
                    <div>
                      <span className="block text-white/40 uppercase text-[10px] tracking-wide">Scheduled Change</span>
                      <span className="text-amber-300 font-semibold">{money(wr.upcoming.base_rate)}/wk from {wr.upcoming.effective_date}</span>
                    </div>
                  )}
                </div>
                {wrEditing ? (
                  <div className="rounded border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-2.5 space-y-2">
                    <div>
                      <label className="text-[11px] text-white/45 block">Reason (required)</label>
                      <input data-testid={`wr-reason-${booking.id}`} className="w-full bg-black border border-white/20 rounded px-2 py-1 text-white text-xs" placeholder="Why is this rate being changed?"
                        value={wrForm.reason} onChange={(e) => setWrForm((p) => ({ ...p, reason: e.target.value }))} />
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`wr-save-${booking.id}`} onClick={() => { if (!wrForm.reason.trim()) { toast.error("A reason is required to change the weekly rate."); return; } saveWeeklyRate(); }} disabled={wrSaving}
                        className="bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                        {wrSaving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Save New Rate
                      </button>
                      <button onClick={() => setWrEditing(false)} className="text-white/40 hover:text-white text-xs">Cancel</button>
                    </div>
                    <p className="text-white/35 text-[11px]">Takes effect on the date above — current and already-billed periods are never rewritten.</p>
                  </div>
                ) : wr.schedule?.length > 0 && (
                  <button data-testid={`wr-clear-${booking.id}`} onClick={clearWeeklyRate} className="text-[11px] text-white/40 hover:text-red-300">Remove last agreed-rate change</button>
                )}
                {wr.schedule?.length > 0 && (
                  <div className="text-[11px] text-white/35 space-y-0.5">
                    {wr.schedule.map((e) => (
                      <div key={e.id}>{e.effective_date}: {money(e.base_rate)}/wk set by {e.set_by}{e.reason ? ` — ${e.reason}` : ""}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reconcile As-Of Date — owner-confirmed caught-up correction, preview-then-commit */}
          <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`reconcile-asof-${booking.id}`}>
            <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40"><CalendarClock size={13} /> Reconcile As-Of Date</span>
            <p className="text-white/35 text-[11px] mt-1">Confirm this customer is caught up through a date — never touches payments/refunds/future weeks/return date.</p>
            <div className="flex flex-wrap items-end gap-2 mt-2">
              <div>
                <label className="text-[11px] text-white/45 block">Caught up through</label>
                <input type="date" data-testid={`rc-asof-${booking.id}`} className="bg-black border border-white/20 rounded px-2 py-1 text-white text-xs"
                  value={rc.as_of_date} onChange={(e) => { setRc((p) => ({ ...p, as_of_date: e.target.value })); setRcPreview(null); }} />
              </div>
              <div>
                <label className="text-[11px] text-white/45 block">Owner confirms they owe (through that date)</label>
                <input type="number" step="0.01" data-testid={`rc-asserted-${booking.id}`} className="w-24 bg-black border border-white/20 rounded px-2 py-1 text-white text-xs"
                  value={rc.asserted_current_due} onChange={(e) => { setRc((p) => ({ ...p, asserted_current_due: e.target.value })); setRcPreview(null); }} />
              </div>
              <button data-testid={`rc-preview-btn-${booking.id}`} onClick={runReconcilePreview} disabled={rcBusy}
                className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-60 flex items-center gap-1.5">
                {rcBusy ? <Loader2 className="animate-spin" size={13} /> : null} Preview
              </button>
            </div>
            {rcPreview && (
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="block text-white/40 text-[10px] uppercase">Billed Through Date</span><span className="text-white/80">{money(rcPreview.billed_through)}</span></div>
                  <div><span className="block text-white/40 text-[10px] uppercase">Current Due Before</span><span className="text-white/80">{money(rcPreview.implied_current_due_before)}</span></div>
                  <div><span className="block text-white/40 text-[10px] uppercase">Correction (removes false charge)</span>
                    <span className={rcPreview.correction_amount < 0 ? "text-red-300" : "text-white/50"} data-testid={`rc-correction-${booking.id}`}>
                      {rcPreview.correction_amount < 0 ? "−" : ""}{money(Math.abs(rcPreview.correction_amount))}
                    </span>
                  </div>
                  <div><span className="block text-white/40 text-[10px] uppercase">Future Scheduled Rent (untouched)</span><span className="text-white/60">{money(rcPreview.future_scheduled_rent)}</span></div>
                  <div><span className="block text-white/40 text-[10px] uppercase">Current Due After</span><span className="text-[#F2B705] font-bold">{money(rcPreview.current_due_balance)}</span></div>
                  {rcPreview.credit_balance > 0 && (
                    <div><span className="block text-white/40 text-[10px] uppercase">Credit Preserved</span><span className="text-emerald-300 font-bold">{money(rcPreview.credit_balance)}</span></div>
                  )}
                </div>
                {Math.abs(rcPreview.correction_amount) >= 0.01 ? (
                  <div className="rounded border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-2.5 space-y-2">
                    <input data-testid={`rc-reason-${booking.id}`} className="w-full bg-black border border-white/20 rounded px-2 py-1 text-white text-xs"
                      placeholder="Reason (required) — e.g. owner-confirmed caught up via Zelle through this date"
                      value={rc.reason} onChange={(e) => setRc((p) => ({ ...p, reason: e.target.value }))} />
                    <button data-testid={`rc-apply-btn-${booking.id}`} onClick={applyReconcile} disabled={rcBusy}
                      className="bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors disabled:opacity-60">
                      Approve &amp; Apply Correction
                    </button>
                  </div>
                ) : (
                  <p className="text-emerald-300 text-xs">Already caught up through this date (or in credit) — nothing invented, nothing to adjust.</p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3">
            <button data-testid={`fin-paylink-${booking.id}`} onClick={copyPay}
              className="border border-white/15 text-white/80 text-xs font-bold px-4 py-2 rounded-md hover:bg-white hover:text-black transition-colors flex items-center gap-1.5">
              <Link2 size={13} /> Copy Pay Link
            </button>
            <button data-testid={`fin-refund-${booking.id}`} onClick={() => setShowRefund(true)}
              className="border border-red-400/30 text-red-300 text-xs font-bold px-4 py-2 rounded-md hover:bg-red-400/10 transition-colors flex items-center gap-1.5">
              <RotateCcw size={13} /> Record Refund
            </button>
          </div>

          {showRefund && (
            <RefundDialog booking={booking} token={token} onAuthError={onAuthError}
              onClose={() => setShowRefund(false)}
              onDone={() => { load(); onSaved?.(); }} />
          )}

          {/* Add charge form */}
          {addingCharge && (
            <div data-testid={`fin-add-charge-form-${booking.id}`} className="mt-3 rounded-md border border-emerald-500/30 bg-[#0C0C0C] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-widest text-emerald-300">Add charge to this rental</span>
                <button onClick={() => { setAddingCharge(false); setCharge({ label: "", amount: "" }); }} className="text-white/40 hover:text-white"><X size={15} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[1, 3, 7].map((d) => (
                  <button key={d} data-testid={`fin-cov-preset-${d}-${booking.id}`} onClick={() => covPreset(d)}
                    className="text-[10px] font-bold border border-[#F2B705]/40 text-[#F2B705] rounded-full px-2.5 py-1 hover:bg-[#F2B705] hover:text-black transition-colors">
                    All-In Coverage ×{d}d — {money(covRate * d)}
                  </button>
                ))}
                {CHARGE_PRESETS.map((p) => (
                  <button key={p} data-testid={`fin-preset-${p.toLowerCase().replace(/ /g, "-")}-${booking.id}`}
                    onClick={() => setCharge((c) => ({ ...c, label: p }))}
                    className="text-[10px] font-bold border border-white/20 text-white/55 rounded-full px-2.5 py-1 hover:border-emerald-400/60 hover:text-emerald-300 transition-colors">
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-[11px] text-white/45 block">Charge label</label>
                  <input data-testid={`fin-charge-label-${booking.id}`} className="w-full bg-transparent border-b border-white/15 focus:border-emerald-400 outline-none py-1 text-sm"
                    placeholder="e.g. All-In Coverage ($25.00/day × 3 days)"
                    value={charge.label} onChange={(e) => setCharge((p) => ({ ...p, label: e.target.value }))} />
                </div>
                <div>
                  <label className="text-[11px] text-white/45 block">Amount ($)</label>
                  <input type="number" step="0.01" data-testid={`fin-charge-amount-${booking.id}`} className={cls}
                    value={charge.amount} onChange={(e) => setCharge((p) => ({ ...p, amount: e.target.value }))} />
                </div>
                <button data-testid={`fin-charge-save-${booking.id}`} onClick={addCharge} disabled={chargeSaving}
                  className="bg-emerald-500 text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                  {chargeSaving ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Add Charge
                </button>
              </div>
              <p className="text-white/35 text-[11px] mt-2">Shows as its own line in the breakdown and raises the balance owed. Use a negative amount for a credit.</p>
            </div>
          )}

          {/* Inline editor (only when adjusting) */}
          {editing && (
            <div data-testid={`fin-editor-${booking.id}`} className="mt-3 rounded-md border border-[#F2B705]/30 bg-[#0C0C0C] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-widest text-[#F2B705]">Adjust financials</span>
                <button onClick={() => { setEditing(false); setF({ total_price: total, discount, amount_paid: paid }); }} className="text-white/40 hover:text-white"><X size={15} /></button>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <div><label className="text-[11px] text-white/45 block">Total charges</label><input type="number" data-testid={`fin-total-${booking.id}`} className={cls} value={f.total_price} onChange={(e) => set("total_price", e.target.value)} /></div>
                <div><label className="text-[11px] text-white/45 block">Discount</label><input type="number" data-testid={`fin-discount-${booking.id}`} className={cls} value={f.discount} onChange={(e) => set("discount", e.target.value)} /></div>
                <div><label className="text-[11px] text-white/45 block">Amount paid</label><input type="number" data-testid={`fin-paid-${booking.id}`} className={cls} value={f.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} /></div>
                <div>
                  <label className="text-[11px] text-white/45 block">Balance owed</label>
                  <span className={`font-display font-bold ${editBalance > 0 ? "text-red-400" : "text-green-400"}`}>{money(editBalance)}</span>
                </div>
                <button data-testid={`fin-save-${booking.id}`} onClick={save} disabled={saving} className="bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
                  {saving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Save
                </button>
              </div>
              <p className="text-white/35 text-[11px] mt-2">Editing the total here overrides the itemized amounts — any difference shows as "Other charges / adjustment" in the breakdown.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Row = ({ label, value, tone }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-white/55">{label}</span>
    <span className={`tabular-nums ${tone === "emerald" ? "text-emerald-400" : "text-white/85"}`}>{value}</span>
  </div>
);
